import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderFilterField } from '@/generated/gql/graphql';
import { OrderQueryFieldGroup } from '~/common/types';
import {
  listOperators,
  operators,
  queryFieldFactory,
  renderPercentageEditor,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';

const NAMESPACE_MAP = {
  [OrderFilterField.orderServiceId]: CuriosityNamespaceType.COMMODITY,
  [OrderFilterField.supplierId]: CuriosityNamespaceType.SUPPLIER,
  [OrderFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
  [OrderFilterField.primaryCommodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [OrderFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [OrderFilterField.supplierIndustryId]: CuriosityNamespaceType.STD_NACE_CLASS,
};

export const useOrderDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.order' });
    return {
      fields: [
        createField({
          name: OrderFilterField.externalId,
          grp: OrderQueryFieldGroup.order,
          operators: operators.text,
        }),
        createField({
          name: OrderFilterField.orderCreationDate,
          grp: OrderQueryFieldGroup.order,
          operators: operators.date,
        }),
        createField({
          name: OrderFilterField.orderVolume,
          grp: OrderQueryFieldGroup.order,
          operators: operators.num,
        }),
        // ---------- SUPPLIER ----------
        createField({
          name: OrderFilterField.supplierId,
          grp: OrderQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        createField({
          name: OrderFilterField.supplierExternalId,
          grp: OrderQueryFieldGroup.supplier,
          operators: operators.text,
        }),
        createField({
          name: OrderFilterField.supplierEmployeeCount,
          grp: OrderQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        createField({
          name: OrderFilterField.supplierIndustryId,
          grp: OrderQueryFieldGroup.supplier,
          operators: operators.textAuto,
        }),

        // ---------- CONTRACT INFO ----------
        createField({
          name: OrderFilterField.contractCreationDate,
          grp: OrderQueryFieldGroup.contract,
          operators: operators.date,
        }),
        createField({
          name: OrderFilterField.contractReceivedProposalsCount,
          grp: OrderQueryFieldGroup.contract,
          operators: operators.num,
        }),
        createField({
          name: OrderFilterField.contractAverageValueContribution,
          grp: OrderQueryFieldGroup.contract,
          operators: operators.num,
        }),
        // ---------- PRODUCTS ----------
        createField({
          name: OrderFilterField.orderProductId,
          grp: OrderQueryFieldGroup.product,
          operators: operators.id,
        }),
        // ---------- COMMODITY GROUP ----------
        createField({
          name: OrderFilterField.primaryCommodityGroupId,
          grp: OrderQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
        createField({
          name: OrderFilterField.commodityGroupId,
          grp: OrderQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
        // ---------- SOURCE SYSTEM ----------
        createField({
          name: OrderFilterField.sourceSystemId,
          grp: OrderQueryFieldGroup.sourceSystem,
          operators: operators.textAuto,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case OrderFilterField.contractCreationDate:
          case OrderFilterField.orderCreationDate:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case OrderFilterField.orderServiceId:
          case OrderFilterField.supplierId:
          case OrderFilterField.sourceSystemId:
          case OrderFilterField.primaryCommodityGroupId:
          case OrderFilterField.commodityGroupId:
          case OrderFilterField.supplierIndustryId:
            return (
              <CuriosityAutocomplete
                ns={NAMESPACE_MAP[field]}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );

          case OrderFilterField.supplierExternalId:
          case OrderFilterField.externalId:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          //
          case OrderFilterField.contractAverageValueContribution:
            return renderPercentageEditor({ operator, otherProps });
          //
          case OrderFilterField.contractReceivedProposalsCount:
          case OrderFilterField.supplierEmployeeCount:
          case OrderFilterField.orderVolume:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          //
          case OrderFilterField.contractCreationDate:
          case OrderFilterField.orderCreationDate:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
